import _ from 'lodash';
import SearchSession from '../entities/SearchSession';
import OfflineDB from './OfflineDB';
import FirebaseManager from "./Firebase/Manager";
import DateUtils from './Dates';

let _search_sessions = {};
let _current_search_token = '';

class SessionManager {
    addSearchSession(searchSession) {
        if (SearchSession.isValid(searchSession)) {
            _search_sessions[searchSession.search_token] = searchSession;
            _current_search_token = searchSession.search_token;

            // optional
            this.clearOldSearchSessions();

            setTimeout(() => {
                searchSession.notifyEvent();
            });
        } else {
            console.warn('SessionManager.addSearchSession got an invalid searchSession', searchSession);
        }
    }

    async getSearchSessionFromOfflineDB(searchToken, recommendationWeights, options = {}) {
        const raw_search_session = await OfflineDB.getSearchSession(searchToken);

        // TODO: more than basic validation?
        if (raw_search_session) {
            let raw_search_terms = raw_search_session.search_terms;
            raw_search_terms.check_in = DateUtils.customDateOnlyParse(raw_search_terms.check_in);
            raw_search_terms.check_out = DateUtils.customDateOnlyParse(raw_search_terms.check_out);

            const search_travel_policy = await OfflineDB.getSearchTravelPolicy(searchToken);
            const raw_search_hotels = await OfflineDB.getSearchHotels(searchToken);
            const raw_search_shallows = await OfflineDB.getSearchShallows(searchToken);
            const raw_search_deals = await OfflineDB.getSearchDeals(searchToken);

            const room_mapping_settings = await OfflineDB.getRoomMappingSettings(searchToken);

            const search_session = new SearchSession(
                searchToken,
                raw_search_terms,
                recommendationWeights,
                {
                    created_timestamp: raw_search_session.created_timestamp,
                    travel_policy: search_travel_policy,
                    room_mapping_settings,
                    completed_delay: true,
                    from_offline_db: true,
                    ...(_.isBoolean(raw_search_session.arbitrip_points_applied) ? { arbitrip_points_applied: raw_search_session.arbitrip_points_applied } : {}),
                    ...options
                }
            );

            search_session.search_results.addHotels(raw_search_hotels, false);
            search_session.search_results.addHotels(raw_search_shallows, true);
            search_session.search_results.addHotelsDeals(search_session, raw_search_deals);

            this.addSearchSession(search_session, options);
            return search_session;
        }

        return null;
    }

    createSearchSession(searchToken, searchTerms, recommendationWeights, options = {}) {
        this.addSearchSession(new SearchSession(searchToken, searchTerms, recommendationWeights, options));
        return this.current_search_session;
    }

    get current_search_session() {
        return _search_sessions[_current_search_token];
    }

    clearSearchSession(searchToken) {
        const old_search_session = _search_sessions[searchToken];
        if (old_search_session) {
            old_search_session.clear();
        }

        delete _search_sessions[searchToken];
    }

    clearOldSearchSessions() {
        const search_tokens = Object.keys(_search_sessions)
            .filter((searchToken) => (searchToken !== _current_search_token));

        for (const searchToken in search_tokens) {
            this.clearSearchSession(searchToken);
        }
    }

    ////////////////////////////////////////

    get current_search_results() {
        return _.get(this.current_search_session, 'search_results');
    }

    getHotelById(hotelId) {
        const search_results = this.current_search_results;
        if (search_results) {
            return search_results.getHotelById(hotelId);
        }

        return null;
    }

    getTravelPolicy() {
        const search_results = this.current_search_results;
        if (search_results) {
            return search_results.travel_policy;
        }

        return null;
    }

    getSearchTerms() {
        const search_session = this.current_search_session;
        if (search_session) {
            return search_session.search_terms;
        }

        return null;
    }

    onSearch(searchResponse) {
        const search_session = this.current_search_session;
        if (search_session) {
            search_session.onSearch(searchResponse);
        }
    }

    onHotels(hotelsResponse) {
        const search_session = this.current_search_session;
        if (search_session) {
            search_session.onHotels(hotelsResponse);
        }
    }

    onRecheck(recheckResponse) {
        const search_session = this.current_search_session;
        if (search_session) {
            search_session.onRecheck(recheckResponse);
        }
    }

    getSearchResults() {
        const search_session = this.current_search_session;
        if (search_session) {
            return search_session.search_results;
        } else {
            return {};
        }
    }

    getHotels() {
        const search_session = this.current_search_session;
        if (search_session) {
            return search_session.search_results.hotels;
        } else {
            return [];
        }
    }

    getTimestamp() {
        const search_session = this.current_search_session;
        if (search_session) {
            return search_session.search_results.timestamp;
        } else {
            return new Date();
        }
    }

    getHotelsObject() {
        const search_session = this.current_search_session;
        if (search_session) {
            return search_session.search_results._hotels;
        } else {
            return {};
        }
    }
}

export default SessionManager;

// from inside
// this.emit(<EVENT_NAME>, <COMMA_SEPARATED_PARAMETERS>);
// e.g. this.emit('change', 'string', 1, {a:2, b:'3'});

// from outside
// session_manager.on(<EVENT_NAME>, (<COMMA_SEPARATED_PARAMETERS>) => {...});
// e.g. session_manager.on('change', (str, num, obj) => {...});
